import { Appointment } from './interface.appointments';
import { EdgeAPIResponse } from './interface.edgeApiResponse';
import { GenericOption } from './interface.vehicle';

// BEGIN GET Offer Edge API Response Interface //
export interface GetOfferEdgeAPIResponse extends EdgeAPIResponse {
  data: {
    offerId: string;
    prospect: Prospect;
    priceAdvisor: string;
    amount: number;
    markupAmounts: {
      [key: string]: number;
    };
    totalOfferAmounts: {
      [key: string]: number;
    };
    expirationDate: string;
    creationDate: string;
    isExpired: boolean;
    quickQuote: boolean;
    status: string;
    offerCode: string;
    offerSourceId: number;
    canBeResubmitted: boolean;
    offerExplanation: string;
    offerExplanationDetails: string[];
    chosenCondition: string;
    valuation?: Valuation[] | null;
    incomingUrl: string;
    offerImages?: OfferImage[] | null;
    appointments?: Appointment[] | null;
    isAppointmentRequestEnabled: boolean;
    accidentQuestionModifiedByDealer: boolean;
    isExclusiveOffer: boolean;
    originalDateCreated: string;
    originalExpirationDate: string;
    consumerInsights: {
      makeOfPreferredModel: string;
      marketLevel: string;
      offerId: number;
      preferredModel: string;
      preferredVehicleType: string;
      yearFrom: number;
      yearTo: number;
    };
    auctionQuestionAnsweredByUser: boolean;
    hasBuyingSignalsSubscription: boolean;
    sellingSignalsWeightingTotal: number;
    sellingSignalSummary: string;
    sellingSignalsEnable: boolean;
    conditionTierSelection: string;
    potentialTaxSaving: number;
    conditionTierList?: ConditionTierList[] | null;
    kbycDisposalInfo: DisposalInfo | null;
    disposalType: string;
    currentWeeklyDepreciation?: number;
    kbbOptionAdjustment?: number;
  };
}
// END GET Offer Edge API Response Interface //

// BEGIN POST Offer Edge API Response Interface //
export interface PostOfferEdgeAPIResponse extends EdgeAPIResponse {
  accidentQuestionModifiedByDealer: boolean;
  amount: number;
  appointments: Appointment[] | null; // fix this
  auctionQuestionAnsweredByUser: boolean;
  canBeResubmitted: boolean;
  chosenCondition: string;
  expirationDate: string;
  incomingUrl: string;
  isAppointmentRequestEnabled: boolean;
  isExclusiveOffer: boolean;
  isExpired: boolean;
  markupAmounts: {
    [key: string]: number;
  };
  offerCode: string;
  offerExplanation: string;
  offerExplanationDetails: OfferExplanationDetail[] | null;
  offerId: string;
  potentialTaxSaving: number;
  priceAdvisor: string;
  prospect: Prospect;
  quickQuote: boolean;
  status: string;
  totalOfferAmounts: {
    [key: string]: number;
  };
  valuation: Valuation[];
}

interface OfferExplanationDetail {
  SortOrder: number;
  ShortVersion: string;
  LongVersion: string;
}

interface Prospect {
  vehicle: {
    yearId: number;
    makeId: number;
    modelId: number;
    trimId: number;
    transmissionId: number;
    engineId: number;
    colorId: number;
    drivetrainId: number;
    vin: string;
    vehicleDescription: {
      year: string;
      make: string;
      model: string;
      trim: string;
      transmission: string;
      engine: string;
      color: string;
      drivetrain: string;
      bodyStyle: string;
    };
    vehicleBuildDataFlags: {
      cadsUsed: boolean;
      transmissionsVinVerified: boolean;
      enginesVinVerified: boolean;
      drivetrainsVinVerified: boolean;
      optionsVinVerified: boolean;
      colorsVinVerified: boolean;
    };
  };
  mileage: number;
  dealerId: number;
  dealerRepId: number;
  dealerRepName: string;
  dealerRepPhone: string;
  isConsumerOffer: boolean;
  dealerComment: string;
  history: History[];
  options: VehicleOption[];
  genericOptions: GenericOption[];
  conditions: Condition[];
  paintMeter: object[];
  contactInfo: {
    firstName?: string;
    lastName?: string;
    email?: string;
    confirmEmail?: string;
    phone?: string;
    legalAcceptance?: boolean;
    zip?: string;
    required: string[];
  };
  replacementVehicle: ReplacementVehicle[];
  dealershipsAndInventory: {
    allocationEntries: EdgeAPIDealerAllocation[];
    buyingCenters: EdgeAPIDealerAllocation[];
    tradeInCenters: EdgeAPIDealerAllocation[];
    closestDealerships: EdgeAPIDealerAllocation[];
  };
  status: string;
  channelId: string;
  cobranding: {
    groupId: string;
    dealerListUrl: string;
  };
}
interface OfferImage {
  id: string;
  offerId: string;
  category: string;
  imageUrl: string;
  thumbnailUrl: string;
}

interface VehicleOption {
  optionId: number;
  categoryName: string;
  displayName: string;
  isSelected: boolean;
  isDefault: boolean;
  isDefaultConfiguration: boolean;
}

interface Condition {
  questionId: string;
  questionType: string;
  label: string;
  maxStringLength: number;
  maximumValue: number;
  minimumValue: number;
  enumChoices: string[];
  selectChoices: SelectChoice[];
  comment: string;
  tags: string[];
  value: string;
  acceptsComment: boolean;
  labelExtended: string;
  manditory: boolean;
}

interface SelectChoice {
  qRef: string;
  value: string;
  acceptsComment: boolean;
  selected: boolean;
  comment: string;
}

interface ReplacementVehicle {
  questionId: string;
  questionType: string;
  label: string;
  value?: string;
  enumChoices?: string[];
  subquestions?: Subquestion[];
  dataReferenceId?: string;
}

interface Subquestion {
  questionId: string;
  questionType: string;
  label: string;
  value?: boolean;
  dataReferenceId?: string;
}

interface EdgeAPIDealerAllocation {
  dealerInfo: {
    dealerId: string;
    distance: number;
    dealershipName: string;
    dealershipAddress: string;
    dealershipCity: string;
    dealershipState: string;
    dealershipZipCode: string;
    dealershipPhone: string;
    dealerWebAddress: string;
    primaryEmailAddress: string;
    dealerPhotoUrl: string;
    moreInventoryUrl: string;
    dealerContact: string;
    longitude: string;
    latitude: string;
    ratingInline: string;
    ratingOverlay: string;
    direction: string;
    voiCount: number;
    voiMake: string;
    voiModel: string;
    voiFilterLink: string;
    specChosen: string;
    interpretChosen: string;
  };
  vehicleInfo: {
    vehicleId: string;
    stockId: string;
    vin: string;
    vehicleType: string;
    year: string;
    make: string;
    model: string;
    bodyStyle: string;
    exteriorColor: string;
    dPrice: number;
    price: string;
    detailsUrl: string;
    vehicleImage: {
      url: string;
      width: string;
      height: string;
      stock: string;
    };
  };
  isExclusiveOffer: boolean;
}

interface History {
  questionId: string;
  questionType: string;
  label: string;
  enumChoices?: (string | null)[] | null;
  value: boolean | string | number;
}

interface ConditionTierList {
  discription: string;
  name: string;
  value: number;
}

export interface Valuation {
  bestMatch: boolean;
  initialPriceType: string;
  kbbBasePrice: number;
  kbbFinalPrice: number;
  mileageAdjustment: number;
  mileageZeroPoint: number;
  uvmmrAdjustedLowAdjustment: number;
  uvmmrAdjustment: number;
  uvmmrAdjustmentType: string;
  uvmmrBasePrice: number;
  uvmmrCiCeilingAdjustment: number;
  uvmmrCiFloorAdjustment: number;
  uvmmrConfidenceAdjustedLow: number;
  uvmmrConfidenceIndicator: number;
  uvmmrFinalPrice: number;
  uvmmrGrade: number;
  uvmmrPriceType: string;
  uvmmrValuation: number;
  uvmmrYearRange: string;
  valuationPrices: {
    priceType: string;
    value: number;
  };
  uvmmrColorAdjustment?: number;
  uvmmrConditionAdjustment?: number;
  uvmmrOptionsAdjustment?: number;
  kbbMileageAdjustment?: number;
  uvmmrEquipmentIndex?: number;
  uvmmrColorScarcityIndex?: number;
  damageDeduct?: number;
}

interface DisposalInfo {
  disposalAcceptedDate: string;
}

export interface GetOfferInProgressAPIResponse extends EdgeAPIResponse {
  data: {
    customerInfo: {
      firstName: string;
      lastName: string;
      email: string;
      zip: string;
      phone: string;
    };
    vehicle: {
      vehicleDescription: {
        vin: string;
        vehicle: string;
        mileage: number;
        color: string;
        twoSetsOfKeys: string;
      };
      vehicleOptions: {
        engine: string;
        transmission: string;
        drive: string;
        installedOptions: string[];
      };
      vehicleHistory: {
        originalOwner: string;
        accidents: string;
        auctionedInLast45Days: string;
        rentalVehicle: string;
      };
      replacementVehicle: {
        lookingFor: string;
        vehicle: string;
      };
    };
  };
}

export const CounterofferStep = {
  Input: 0,
  Review: 1,
  Submitted: 2,
};

export interface disposalOptions {
  isKelleyBuysYourCar: boolean;
  isInstantCashOffer: boolean;
  isKelleyBuysYourCarFakeDoor: boolean;
  existingOfferId: string;
  existingKbycOfferId: string;
}

export type ScrollMilestones = {
  [key: number]: boolean;
};
